import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import css from './IdlePromptPopup.module.css';
import Icons from '../Icons/Icons';
import { FormattedMessage } from 'react-intl';
import { StringBoolean } from '../../util/enums';
import Modal from '../Modal/Modal';

// Global variable to track if popup was shown (resets on hard reload)
let popupShownInCurrentSession = false;

const IdlePromptPopup = ({
  content,
  delay = 0,
  onManageDisableScrolling,
  suppressOnMouseMove = false,
  suppressOnFocus = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isSupplierSignup = location.pathname === '/signup-supplier';
  const isCustomerSignup = location.pathname === '/signup-customer' || location.pathname === '/startyourproject';
  const timerRef = useRef(null);
  const isFocusedRef = useRef(false);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isAuth = localStorage.getItem('userIsAuthenticated') === StringBoolean.TRUE;

    // Don't show if user is authenticated or popup was already shown in this session
    if (isAuth || popupShownInCurrentSession) return;

    const startTimer = () => {
      clearTimeout(timerRef.current);
      if (isFocusedRef.current) return;
      timerRef.current = setTimeout(() => {
        setIsOpen(true);
        popupShownInCurrentSession = true;
      }, delay);
    };

    startTimer();
    hasStartedRef.current = true;

    // Mouse move suppression: only restart timer if user became idle (stopped moving)
    // We debounce — only restart after mouse stops moving for 2s
    let mouseMoveDebounce = null;
    const handleMouseMove = suppressOnMouseMove
      ? () => {
          clearTimeout(mouseMoveDebounce);
          clearTimeout(timerRef.current);
          mouseMoveDebounce = setTimeout(() => {
            // Mouse has stopped — start the idle timer
            startTimer();
          }, 2000);
        }
      : null;

    const handleFocusIn = suppressOnFocus.length > 0
      ? (e) => {
          const matched = suppressOnFocus.some(sel => e.target.matches(sel));
          if (matched) {
            isFocusedRef.current = true;
            clearTimeout(timerRef.current);
          }
        }
      : null;

    const handleFocusOut = suppressOnFocus.length > 0
      ? (e) => {
          const matched = suppressOnFocus.some(sel => e.target.matches(sel));
          if (matched) {
            isFocusedRef.current = false;
            startTimer();
          }
        }
      : null;

    if (handleMouseMove) document.addEventListener('mousemove', handleMouseMove);
    if (handleFocusIn) document.addEventListener('focusin', handleFocusIn);
    if (handleFocusOut) document.addEventListener('focusout', handleFocusOut);

    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(mouseMoveDebounce);
      if (handleMouseMove) document.removeEventListener('mousemove', handleMouseMove);
      if (handleFocusIn) document.removeEventListener('focusin', handleFocusIn);
      if (handleFocusOut) document.removeEventListener('focusout', handleFocusOut);
    };
  }, [delay, suppressOnMouseMove, suppressOnFocus]);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Modal
      id="IdlePromptPopup"
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal={true}
      isIdlePromptModal={true}
      lightCloseButton={false}
      className={css.modalWrapper}
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
    >
      <div className={css.authContentWrapper}>
        <div className={css.authContentImg}>
          <div className={css.authContentImgDetails}>
            <Icons name="stars" />
            {isSupplierSignup ? (
              <>
                <h1>
                  Join the UK's <span className={css.count}>Fastest-Growing</span> Construction Marketplace
                </h1>
                <p>
                  Showcase your work to self-builders and developers — upload your first 5 case studies completely free.
                </p>
              </>
            ) : isCustomerSignup ? (
              <>
                <h1>
                  Start Your Build With <span className={css.count}>Confidence</span>
                </h1>
                <p>
                  Create a free profile and complete your project brief so we can match you with some builders and suppliers
                </p>
              </>
            ) : (
              <>
                <h1>
                  Welcome{' '}
                  <span className={css.count}>Back</span>
                </h1>
                <p>
                  Log in to access your account and continue where you left off.
                </p>
              </>
            )}
          </div>
        </div>
        <div className={css.authContentInfo}>
          {content}
        </div>
      </div>
    </Modal>
  );
};

export default IdlePromptPopup;
